/**
 * types/sync/SyncSettings.ts
 * Local sync settings cache + device-specific state
 *
 * Architecture (Task 2.6 - Option A.1):
 * - SOURCE OF TRUTH: Supabase user_sync_settings (for universal preferences)
 * - CACHE: IndexedDB stores last-known-good server settings for offline access
 * - DEVICE-SPECIFIC: directoryId is unique per device/browser (local provider only)
 *
 * This type serves TWO purposes:
 * 1. Device-specific state: directoryId (lives only in IndexedDB, never synced to server)
 * 2. Offline cache: Universal preferences mirrored from server for offline resilience
 *
 * Data flow:
 * - Read: UserSyncSettingsService tries Supabase → falls back to this cache → defaults
 * - Write: UserSyncSettingsService writes Supabase + updates this cache
 */

import type { SyncProviderType } from './SyncProvider';

/**
 * Conflict resolution strategy options
 */
export type ConflictResolutionStrategy =
  | 'ask' // Prompt user to choose
  | 'keep-local' // Always keep local version
  | 'keep-remote' // Always keep remote version
  | 'last-write-wins'; // Use most recent updatedAt timestamp

/**
 * Sync settings - device-specific state + offline cache
 *
 * Stored in IndexedDB with dual purpose:
 * 1. Device-specific directoryId (source of truth, never synced to server)
 * 2. Cached copy of server settings for offline access (updated on every server write)
 *
 * IMPORTANT: All fields except directoryId are CACHE of server state.
 * Source of truth for universal preferences is Supabase user_sync_settings.
 */
export interface SyncSettings {
  /** Unique identifier (typically one record per device/browser) */
  id: string;

  /** ISO timestamp of creation */
  createdAt: string;

  /** ISO timestamp of last update */
  updatedAt: string;

  // ===== DEVICE-SPECIFIC STATE (source of truth) =====

  /**
   * Unique identifier for local sync directory
   *
   * Source of truth: IndexedDB only (never synced to server)
   * Used for: Conflict detection when switching local directories
   * Relevant only when: activeSyncProvider === 'local'
   */
  directoryId: string | null;

  // ===== CACHED SERVER STATE (for offline access) =====
  // All fields below are CACHE mirroring Supabase user_sync_settings
  // Source of truth: Supabase (these are fallback when offline)

  /** Currently active sync provider (CACHED from server) */
  activeSyncProvider: SyncProviderType | null;

  /** Whether automatic background sync is enabled (CACHED from server) */
  syncEnabled: boolean;

  /** Sync interval in minutes (CACHED from server) */
  syncInterval: number;

  /** Whether to sync when closing the app/tab (CACHED from server) */
  syncOnSessionClose: boolean;

  /** Whether to sync when logging in (CACHED from server) */
  syncOnLogin: boolean;

  /** Strategy for resolving sync conflicts (CACHED from server) */
  conflictResolution: ConflictResolutionStrategy;

  // ===== CACHE METADATA =====

  /**
   * Last successful sync with server
   *
   * null = never synced (cached fields are stale/default)
   * ISO timestamp = last time we successfully read from Supabase
   *
   * Used to: Determine if offline cache is trustworthy
   */
  lastServerSync: string | null;
}

/**
 * Default sync settings values
 *
 * Used when creating settings for the first time.
 * These match the server defaults in UserSyncSettings.
 */
export const DEFAULT_SYNC_SETTINGS: Omit<SyncSettings, 'id' | 'createdAt' | 'updatedAt'> = {
  // Device-specific
  directoryId: null, // No directory connected initially

  // Cached server state (matches server defaults)
  activeSyncProvider: null, // No provider configured initially
  syncEnabled: false, // User must explicitly opt-in
  syncInterval: 1, // 1 minute default (frequent syncs reduce data loss window)
  syncOnSessionClose: true, // Safe default
  syncOnLogin: false, // Avoid surprise syncs on login
  conflictResolution: 'last-write-wins', // Simplest default

  // Cache metadata
  lastServerSync: null, // Never synced yet
};
